import i18n from 'src/i18n'
import React, { Component, ErrorInfo, ReactNode } from 'react'

// The only class component left in this codebase, and it has to be one:
// componentDidCatch / getDerivedStateFromError have no hook equivalent. React
// still offers no way to catch a render error from a function component.
// Please don't "modernize" this file (ENR-09a, see
// docs/features/enr-09-carnet-incidents.md).

interface Props {
  children: ReactNode
  // Wired in phase 2. Left optional so phase 1 ships on its own.
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  hasError: boolean
}

// 🔴 This screen deliberately uses NO Chakra and NO useTranslation, which the
// rest of the app uses everywhere. Both are React contexts mounted INSIDE App
// (see App.tsx: I18nextProvider, ChakraProvider), and this boundary sits ABOVE
// App on purpose — an error thrown by a provider is precisely the case where
// the white screen is total. Reaching for either context here would render the
// raw translation keys, or throw a second time inside the fallback, which is a
// render loop: the very white screen this feature removes.
//
// Translations still come from the locale files: i18n.t() reads the singleton
// directly, which is initialized on import, no provider involved. Styles are
// inline because there is no theme to read.
//
// text() wraps i18n.t() because the i18next signature (unlike react-i18next's)
// widens to TFunctionResult, which TypeScript won't accept as a React child.
// String() rather than a cast: an `as` would silence the compiler exactly where
// we want it talking.
const text = (key: string) => String(i18n.t(key))
function ErrorScreen() {
  const isDark =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches

  const colors = isDark
    ? { bg: '#1a202c', fg: '#f7fafc', muted: '#a0aec0' }
    : { bg: '#ffffff', fg: '#1a202c', muted: '#718096' }

  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: colors.bg,
        color: colors.fg,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: '28rem' }}>
        <div style={{ fontSize: '2.5rem' }} aria-hidden>
          🩹
        </div>
        <h1 style={{ fontSize: '1.25rem', margin: '1rem 0 0.75rem' }}>
          {text('ErrorBoundary.title')}
        </h1>
        <p style={{ color: colors.muted, margin: '0 0 1.5rem' }}>
          {text('ErrorBoundary.description')}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            padding: '0.5rem 1.25rem',
            borderRadius: '0.375rem',
            border: 'none',
            background: '#3182ce',
            color: '#ffffff',
            fontSize: '1rem',
            cursor: 'pointer',
          }}
        >
          {text('ErrorBoundary.reload')}
        </button>
      </div>
    </div>
  )
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Guarded: a reporter that throws would take the boundary down with it.
    try {
      this.props.onError?.(error, info)
    } catch {
      // Nothing to do — losing the report is better than losing the screen.
    }
  }

  render() {
    return this.state.hasError ? <ErrorScreen /> : this.props.children
  }
}
